import { useState, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import Card, { CardHeader } from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import SearchableSelect from '../../../components/ui/SearchableSelect'
import Badge from '../../../components/ui/Badge'
import Table from '../../../components/ui/Table'
import Spinner from '../../../components/ui/Spinner'
import api from '../../../lib/api'
import { formatCurrency, formatDate } from '../../../lib/formatters'
import type { Doctor, Invoice, LabProfile } from '../../../types'
import toast from 'react-hot-toast'

// Custom hook for debounced value
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)
  
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  
  return debouncedValue
}


// ─── Types ───────────────────────────────────────────────────

interface StatementSummary {
  totalWorkOrders: number
  completedOrders: number
  pendingOrders: number
  totalInvoiced: number
  totalPaid: number
  totalOutstanding: number
  totalOverdue: number
  activeDoctors: number
  doctorsWithDues: number
  collectionRate: number
}

interface DoctorStatement {
  doctor: Doctor
  workOrders: number
  invoiceCount: number
  totalInvoiced: number
  totalPaid: number
  balance: number
  overdueAmount: number
  lastInvoiceDate?: string
  lastPaymentDate?: string
}

interface PaymentMethodBreakdown {
  method: string
  count: number
  amount: number
}

interface StatementsData {
  summary: StatementSummary
  doctors: DoctorStatement[]
  paymentMethods: PaymentMethodBreakdown[]
}

// ─── Helpers ─────────────────────────────────────────────────

const getPeriodOptions = () => {
  const options: Array<{ value: string; label: string; sublabel?: string; group?: string }> = []
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth()

  // "All Time" always first
  options.push({ value: '', label: 'All Time', sublabel: 'Complete history' })

  // Recent 6 months - no group, most recent first (these show by default)
  for (let i = 0; i < 6; i++) {
    const date = new Date(currentYear, currentMonth - i, 1)
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    const isCurrentMonth = i === 0
    options.push({
      value,
      label,
      sublabel: isCurrentMonth ? 'Current month' : undefined,
    })
  }

  // Older months (7-18) - grouped by year, shown on "Show more" or search
  for (let i = 6; i < 18; i++) {
    const date = new Date(currentYear, currentMonth - i, 1)
    const year = date.getFullYear()
    const value = `${year}-${String(date.getMonth() + 1).padStart(2, '0')}`
    const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    options.push({ value, label, group: `${year}` })
  }

  return options
}

const periodOptions = getPeriodOptions()

const formatMethod = (m: string) => {
  const labels: Record<string, string> = {
    cash: 'Cash', upi: 'UPI', imps: 'IMPS', neft: 'NEFT', rtgs: 'RTGS',
    cheque: 'Cheque', card: 'Card', bank_transfer: 'Bank Transfer',
  }
  return labels[m] || m
}

const getMethodIcon = (m: string) => {
  const icons: Record<string, string> = {
    cash: 'fa-money-bill-wave', upi: 'fa-mobile-alt', imps: 'fa-exchange-alt',
    neft: 'fa-university', rtgs: 'fa-university', cheque: 'fa-money-check',
    card: 'fa-credit-card', bank_transfer: 'fa-university',
  }
  return icons[m] || 'fa-receipt'
}

const getSelectedPeriodLabel = (period: string) => {
  if (!period) return 'All Time'
  const opt = periodOptions.find(o => o.value === period)
  return opt?.label || period
}

// ─── Component ───────────────────────────────────────────────

export default function DuesOverviewPage() {
  const [selectedMonth, setSelectedMonth] = useState('')
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('')
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  const printRef = useRef<HTMLDivElement>(null)

  // Debounce search to avoid excessive API calls
  const debouncedSearch = useDebouncedValue(search, 300)

  const { data, isLoading } = useQuery<StatementsData>({
    queryKey: ['statements', 'overview', selectedMonth, debouncedSearch, filter],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (selectedMonth) params.append('month', selectedMonth)
      if (debouncedSearch) params.append('search', debouncedSearch)
      if (filter) params.append('filter', filter)
      const { data } = await api.get(`/statements/overview?${params}`)
      return data
    },
  })

  const { data: doctorInvoices } = useQuery({
    queryKey: ['statements', 'doctor', selectedDoctor?.id],
    queryFn: async () => {
      const { data } = await api.get<{ invoices: Invoice[] }>(`/statements/doctor/${selectedDoctor?.id}`)
      return data.invoices
    },
    enabled: !!selectedDoctor,
  })

  const { data: labProfile } = useQuery({
    queryKey: ['settings', 'lab-profile'],
    queryFn: async () => {
      const { data } = await api.get<LabProfile>('/settings/lab-profile')
      return data
    },
  })

  const summary = data?.summary
  const doctors = data?.doctors ?? []
  const paymentMethods = data?.paymentMethods ?? []

  const collectionRate = summary && summary.totalInvoiced > 0
    ? ((summary.totalPaid / summary.totalInvoiced) * 100)
    : 0

  // ─── Print ──────────────────────────────────────────────────

  const handlePrint = () => {
    window.print()
  }

  // ─── CSV Export ─────────────────────────────────────────────

  const handleExport = () => {
    if (!doctors.length) {
      toast.error('No data to export')
      return
    }
    setIsExporting(true)
    try {
      const rows = [
        ['Balance Statement Report'],
        ['Period', getSelectedPeriodLabel(selectedMonth)],
        ['Generated', new Date().toLocaleDateString()],
        ['Filter', filter || 'All'],
        [''],
        ['SUMMARY'],
        ['Total Work Orders', String(summary?.totalWorkOrders ?? 0)],
        ['Total Invoiced', String(summary?.totalInvoiced ?? 0)],
        ['Total Paid', String(summary?.totalPaid ?? 0)],
        ['Total Outstanding', String(summary?.totalOutstanding ?? 0)],
        ['Total Overdue', String(summary?.totalOverdue ?? 0)],
        ['Collection Rate %', collectionRate.toFixed(1)],
        [''],
        ['DOCTOR-WISE BREAKDOWN'],
        ['Doctor', 'Clinic', 'Code', 'Work Orders', 'Invoices', 'Invoiced', 'Paid', 'Balance', 'Overdue', 'Last Invoice', 'Last Payment'],
        ...doctors.map(d => [
          d.doctor.name,
          d.doctor.clinicName || '',
          (d.doctor as any).code || '',
          String(d.workOrders),
          String(d.invoiceCount),
          String(d.totalInvoiced),
          String(d.totalPaid),
          String(d.balance),
          String(d.overdueAmount),
          d.lastInvoiceDate ? formatDate(d.lastInvoiceDate) : '-',
          d.lastPaymentDate ? formatDate(d.lastPaymentDate) : '-',
        ]),
        [''],
        ['PAYMENT METHOD BREAKDOWN'],
        ['Method', 'Count', 'Amount'],
        ...paymentMethods.map(p => [formatMethod(p.method), String(p.count), String(p.amount)]),
      ]
      const csv = rows.map(r => r.join(',')).join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `balance_statement_${selectedMonth || 'all'}_${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      toast.success('Report exported as CSV')
    } finally {
      setIsExporting(false)
    }
  }

  // ─── Table columns ─────────────────────────────────────────

  const columns = [
    {
      key: 'doctor',
      header: 'Doctor / Clinic',
      render: (item: DoctorStatement) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{item.doctor.name}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{item.doctor.clinicName || '-'}</p>
        </div>
      ),
    },
    {
      key: 'workOrders',
      header: 'Orders',
      render: (item: DoctorStatement) => (
        <span className="text-gray-700 dark:text-gray-300 font-medium">{item.workOrders}</span>
      ),
    },
    {
      key: 'totalInvoiced',
      header: 'Invoiced',
      render: (item: DoctorStatement) => (
        <span className="text-gray-700 dark:text-gray-300">{formatCurrency(item.totalInvoiced)}</span>
      ),
    },
    {
      key: 'totalPaid',
      header: 'Paid',
      render: (item: DoctorStatement) => (
        <span className="text-green-600 font-medium">{formatCurrency(item.totalPaid)}</span>
      ),
    },
    {
      key: 'balance',
      header: 'Balance',
      render: (item: DoctorStatement) => (
        <span className={`font-semibold ${item.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
          {formatCurrency(item.balance)}
        </span>
      ),
    },
    {
      key: 'overdueAmount',
      header: 'Overdue',
      render: (item: DoctorStatement) =>
        item.overdueAmount > 0 ? (
          <Badge variant="error">{formatCurrency(item.overdueAmount)}</Badge>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        ),
    },
    {
      key: 'lastPayment',
      header: 'Last Payment',
      render: (item: DoctorStatement) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {item.lastPaymentDate ? formatDate(item.lastPaymentDate) : '-'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (item: DoctorStatement) => (
        <div className="flex justify-end gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); setSelectedDoctor(item.doctor) }}
            className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
            title="View Statement"
          >
            <i className="fas fa-file-alt text-sm"></i>
          </button>
          <Link
            to={`/payments?new=1&doctorId=${item.doctor.id}`}
            className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
            title="Record Payment"
            onClick={(e) => e.stopPropagation()}
          >
            <i className="fas fa-plus text-sm"></i>
          </Link>
        </div>
      ),
    },
  ]

  // ─── Render ─────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <>
      {/* Print-only styles */}
      <style>{`
        @media print {
          .stmt-page { font-size:7.5pt; line-height:1.25; color:#111; page-break-after:always; }
          .stmt-page:last-child { page-break-after:auto; }
          .stmt-hdr { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:1.5pt solid #4f46e5; padding-bottom:4px; margin-bottom:6px; }
          .stmt-ftr { display:flex; justify-content:space-between; border-top:0.5pt solid #ccc; padding-top:3px; margin-top:12px; font-size:6.5pt; color:#888; }
          .stmt-tbl { width:100%; border-collapse:collapse; font-size:7pt; }
          .stmt-tbl th { background:#f3f4f6; font-weight:600; text-transform:uppercase; font-size:6pt; padding:2px 3px; border:0.5pt solid #ccc; text-align:left; }
          .stmt-tbl td { padding:2px 3px; border:0.5pt solid #ccc; }
          .stmt-tbl .r { text-align:right; }
          .stmt-grid { display:grid !important; grid-template-columns:repeat(4,1fr); gap:4px; margin-bottom:6px; }
          .stmt-grid3 { display:grid !important; grid-template-columns:repeat(3,1fr); gap:4px; margin-bottom:6px; }
          .stmt-card { border:0.5pt solid #ccc; border-radius:2px; padding:4px 6px; text-align:center; }
        }
      `}</style>

      <div ref={printRef} className="print-area space-y-6 no-print">
        {/* ── Header ─────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Balance Statements</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {selectedMonth ? getSelectedPeriodLabel(selectedMonth) : 'All-time'} overview of orders, invoices & collections
            </p>
          </div>
          <div className="flex gap-2 no-print">
            <Link to="/statements/monthly">
              <Button>
                <i className="fas fa-file-invoice mr-2"></i>Monthly Statement
              </Button>
            </Link>
            <Button variant="secondary" onClick={handlePrint}>
              <i className="fas fa-print mr-2"></i>Print
            </Button>
            <Button variant="secondary" onClick={handleExport} loading={isExporting}>
              <i className="fas fa-download mr-2"></i>Export CSV
            </Button>
          </div>
        </div>

        {/* ── Filters ────────────────────────────────────── */}
        <Card className="no-print">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="Search doctor, clinic, code..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            <SearchableSelect
              value={selectedMonth}
              onChange={(val) => setSelectedMonth(val)}
              placeholder="Period"
              icon="fa-calendar"
              options={periodOptions}
              minWidth="220px"
              maxVisible={7}
            />
            <SearchableSelect
              value={filter}
              onChange={(val) => setFilter(val)}
              placeholder="Status"
              icon="fa-filter"
              minWidth="180px"
              options={[
                { value: '', label: 'All Doctors', sublabel: 'No filter applied' },
                { value: 'outstanding', label: 'With Outstanding', sublabel: 'Has unpaid balance' },
                { value: 'overdue', label: 'Overdue Only', sublabel: 'Past due date' },
              ]}
            />
          </div>
        </Card>

        {/* ── Summary Cards (Balance Sheet) ──────────────── */}
        <div className="print-break">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            <i className="fas fa-chart-bar mr-2 text-primary-500"></i>
            Balance Sheet — {getSelectedPeriodLabel(selectedMonth)}
          </h2>

          {/* Row 1: Orders + Invoicing */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            <Card>
              <div className="text-center">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <i className="fas fa-clipboard-list text-blue-600"></i>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{summary?.totalWorkOrders ?? 0}</p>
                <p className="text-xs text-gray-500 mt-1">Total Orders</p>
                <div className="flex justify-center gap-3 mt-2 text-xs">
                  <span className="text-green-600">{summary?.completedOrders ?? 0} done</span>
                  <span className="text-amber-600">{summary?.pendingOrders ?? 0} pending</span>
                </div>
              </div>
            </Card>
            <Card>
              <div className="text-center">
                <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <i className="fas fa-file-invoice-dollar text-indigo-600"></i>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(summary?.totalInvoiced ?? 0)}</p>
                <p className="text-xs text-gray-500 mt-1">Total Invoiced</p>
              </div>
            </Card>
            <Card>
              <div className="text-center">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <i className="fas fa-check-circle text-green-600"></i>
                </div>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(summary?.totalPaid ?? 0)}</p>
                <p className="text-xs text-gray-500 mt-1">Total Collected</p>
              </div>
            </Card>
            <Card>
              <div className="text-center">
                <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <i className="fas fa-percentage text-emerald-600"></i>
                </div>
                <p className="text-2xl font-bold text-emerald-600">{collectionRate.toFixed(1)}%</p>
                <p className="text-xs text-gray-500 mt-1">Collection Rate</p>
              </div>
            </Card>
          </div>

          {/* Row 2: Outstanding highlights */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-100 text-sm">Outstanding</p>
                  <p className="text-3xl font-bold mt-1">{formatCurrency(summary?.totalOutstanding ?? 0)}</p>
                </div>
                <i className="fas fa-exclamation-circle text-4xl text-red-200"></i>
              </div>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">Overdue</p>
                  <p className="text-3xl font-bold mt-1">{formatCurrency(summary?.totalOverdue ?? 0)}</p>
                </div>
                <i className="fas fa-clock text-4xl text-amber-200"></i>
              </div>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Doctors with Dues</p>
                  <p className="text-3xl font-bold mt-1">{summary?.doctorsWithDues ?? 0} <span className="text-lg font-normal text-blue-200">/ {summary?.activeDoctors ?? 0}</span></p>
                </div>
                <i className="fas fa-user-md text-4xl text-blue-200"></i>
              </div>
            </Card>
          </div>
        </div>

        {/* ── Payment Methods Breakdown ──────────────────── */}
        {paymentMethods.length > 0 && (
          <Card className="print-break">
            <CardHeader title="Collections by Payment Method" subtitle={getSelectedPeriodLabel(selectedMonth)} />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
              {paymentMethods.map((pm) => (
                <div key={pm.method} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="w-9 h-9 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`fas ${getMethodIcon(pm.method)} text-primary-600 text-sm`}></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 truncate">{formatMethod(pm.method)}</p>
                    <p className="font-semibold text-gray-900 dark:text-white text-sm">{formatCurrency(pm.amount)}</p>
                    <p className="text-xs text-gray-400">{pm.count} txn{pm.count !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* ── Doctor-wise Table ──────────────────────────── */}
        <div className="print-break">
          <Card padding="none">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <CardHeader
                title="Doctor-wise Breakdown"
                subtitle={`${doctors.length} doctor${doctors.length !== 1 ? 's' : ''} • Sorted by balance (highest first)`}
                action={
                  <div className="flex items-center gap-2 text-sm text-gray-500 no-print">
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-red-500 rounded-full inline-block"></span> Overdue
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-amber-500 rounded-full inline-block"></span> Outstanding
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-green-500 rounded-full inline-block"></span> Clear
                    </span>
                  </div>
                }
              />
            </div>
            <Table
              columns={columns}
              data={doctors}
              loading={isLoading}
              keyExtractor={(item) => item.doctor.id}
              emptyMessage={
                filter === 'overdue' ? 'No overdue payments found — great!'
                  : filter === 'outstanding' ? 'No outstanding balances'
                  : 'No data for this period'
              }
              onRowClick={(item) => setSelectedDoctor(item.doctor)}
            />
          </Card>
        </div>

      </div>

      {/* ── PRINT-ONLY LAYOUT ────────────────────────────────────── */}
      <div className="print-only hidden">
        {/* Page 1: Summary + doctors (if fits) */}
        <div className="stmt-page">
          <div className="stmt-hdr">
            <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
              <div style={{width:24,height:24,background:'#4f46e5',borderRadius:4,display:'flex',alignItems:'center',justifyContent:'center'}}>
                <i className="fas fa-tooth" style={{color:'#fff',fontSize:'10px'}}></i>
              </div>
              <div>
                <div style={{fontWeight:700,fontSize:'9pt'}}>{labProfile?.name ?? 'Jasmine Dental Lab'}</div>
                <div style={{fontSize:'6.5pt',color:'#666'}}>{labProfile?.phone}{labProfile?.email ? ` | ${labProfile.email}` : ''}</div>
              </div>
            </div>
            <div style={{textAlign:'right'}}>
              <div style={{fontSize:'8pt',fontWeight:700,color:'#4f46e5'}}>BALANCE STATEMENT</div>
              <div style={{fontSize:'8pt',fontWeight:700}}>{getSelectedPeriodLabel(selectedMonth)}</div>
              <div style={{fontSize:'6.5pt',color:'#666'}}>Generated: {new Date().toLocaleDateString()}</div>
            </div>
          </div>

          {/* Summary cards */}
          <div style={{fontSize:'6pt',fontWeight:600,textTransform:'uppercase',color:'#888',marginBottom:3}}>Financial Summary</div>
          <div className="stmt-grid">
            <div className="stmt-card">
              <div style={{fontSize:'10pt',fontWeight:700}}>{summary?.totalWorkOrders ?? 0}</div>
              <div style={{fontSize:'6pt',color:'#888'}}>Total Orders</div>
              <div style={{fontSize:'6pt',color:'#aaa'}}>{summary?.completedOrders ?? 0} done / {summary?.pendingOrders ?? 0} pending</div>
            </div>
            <div className="stmt-card">
              <div style={{fontSize:'8pt',fontWeight:700}}>{formatCurrency(summary?.totalInvoiced ?? 0)}</div>
              <div style={{fontSize:'6pt',color:'#888'}}>Total Invoiced</div>
            </div>
            <div className="stmt-card">
              <div style={{fontSize:'8pt',fontWeight:700,color:'#16a34a'}}>{formatCurrency(summary?.totalPaid ?? 0)}</div>
              <div style={{fontSize:'6pt',color:'#888'}}>Total Collected</div>
            </div>
            <div className="stmt-card">
              <div style={{fontSize:'10pt',fontWeight:700,color:'#059669'}}>{collectionRate.toFixed(1)}%</div>
              <div style={{fontSize:'6pt',color:'#888'}}>Collection Rate</div>
            </div>
          </div>
          <div className="stmt-grid3">
            <div className="stmt-card" style={{border:'1pt solid #fca5a5',background:'#fef2f2'}}>
              <div style={{fontSize:'8pt',fontWeight:700,color:'#dc2626'}}>{formatCurrency(summary?.totalOutstanding ?? 0)}</div>
              <div style={{fontSize:'6pt',color:'#b91c1c'}}>Outstanding</div>
            </div>
            <div className="stmt-card" style={{border:'1pt solid #fcd34d',background:'#fffbeb'}}>
              <div style={{fontSize:'8pt',fontWeight:700,color:'#d97706'}}>{formatCurrency(summary?.totalOverdue ?? 0)}</div>
              <div style={{fontSize:'6pt',color:'#92400e'}}>Overdue</div>
            </div>
            <div className="stmt-card">
              <div style={{fontSize:'8pt',fontWeight:700}}>{summary?.doctorsWithDues ?? 0} <span style={{fontSize:'7pt',fontWeight:400,color:'#888'}}>/ {summary?.activeDoctors ?? 0}</span></div>
              <div style={{fontSize:'6pt',color:'#888'}}>Doctors with Dues</div>
            </div>
          </div>

          {/* Payment methods (compact inline) */}
          {paymentMethods.length > 0 && (
            <div style={{marginBottom:6}}>
              <div style={{fontSize:'6pt',fontWeight:600,textTransform:'uppercase',color:'#888',marginBottom:2}}>Collections by Method</div>
              <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                {paymentMethods.map(pm => (
                  <span key={pm.method} style={{fontSize:'6.5pt',border:'0.5pt solid #ccc',borderRadius:2,padding:'1px 4px'}}>
                    {formatMethod(pm.method)}: <strong>{formatCurrency(pm.amount)}</strong> ({pm.count})
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Doctor table (on same page if <=15 doctors) */}
          {doctors.length > 0 && (
            <div>
              <div style={{fontSize:'6pt',fontWeight:600,textTransform:'uppercase',color:'#888',marginBottom:2}}>Doctor-wise Breakdown</div>
              <table className="stmt-tbl">
                <thead>
                  <tr>
                    <th>Doctor / Clinic</th>
                    <th className="r">Orders</th>
                    <th className="r">Invoiced</th>
                    <th className="r">Paid</th>
                    <th className="r">Balance</th>
                    <th className="r">Overdue</th>
                    <th className="r">Last Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {doctors.map((d) => (
                    <tr key={d.doctor.id}>
                      <td><span style={{fontWeight:500}}>{d.doctor.name}</span>{d.doctor.clinicName ? <span style={{color:'#888',marginLeft:3,fontSize:'6pt'}}>({d.doctor.clinicName})</span> : ''}</td>
                      <td className="r">{d.workOrders}</td>
                      <td className="r">{formatCurrency(d.totalInvoiced)}</td>
                      <td className="r" style={{color:'#16a34a'}}>{formatCurrency(d.totalPaid)}</td>
                      <td className="r" style={{fontWeight:600,color:d.balance > 0 ? '#dc2626' : '#16a34a'}}>{formatCurrency(d.balance)}</td>
                      <td className="r" style={{color:d.overdueAmount > 0 ? '#dc2626' : '#aaa',fontWeight:d.overdueAmount > 0 ? 600 : 400}}>{d.overdueAmount > 0 ? formatCurrency(d.overdueAmount) : '-'}</td>
                      <td className="r" style={{fontSize:'6pt'}}>{d.lastPaymentDate ? formatDate(d.lastPaymentDate) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{fontWeight:700,background:'#f3f4f6'}}>
                    <td>TOTAL</td>
                    <td className="r">{summary?.totalWorkOrders ?? 0}</td>
                    <td className="r">{formatCurrency(summary?.totalInvoiced ?? 0)}</td>
                    <td className="r" style={{color:'#16a34a'}}>{formatCurrency(summary?.totalPaid ?? 0)}</td>
                    <td className="r" style={{color:'#dc2626'}}>{formatCurrency(summary?.totalOutstanding ?? 0)}</td>
                    <td className="r" style={{color:'#dc2626'}}>{formatCurrency(summary?.totalOverdue ?? 0)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <div className="stmt-ftr">
            <span>{labProfile?.address}</span>
            <span style={{fontWeight:600}}>Balance Statement &nbsp;|&nbsp; Page 1 of 1</span>
            <span>Printed: {new Date().toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      {/* ── Doctor Detail Drawer ─────────────────────────── */}
      {selectedDoctor && (
        <div className="fixed inset-0 z-50 flex no-print">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedDoctor(null)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white dark:bg-gray-800 shadow-xl overflow-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {selectedDoctor.name}
                </h2>
                <p className="text-sm text-gray-500">{selectedDoctor.clinicName}</p>
              </div>
              <button
                onClick={() => setSelectedDoctor(null)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Doctor summary */}
              {(() => {
                const d = doctors.find(doc => doc.doctor.id === selectedDoctor.id)
                if (!d) return null
                return (
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-gray-900 dark:text-white">{d.workOrders}</p>
                      <p className="text-xs text-gray-500">Orders</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-green-600">{formatCurrency(d.totalPaid)}</p>
                      <p className="text-xs text-gray-500">Paid</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                      <p className={`text-lg font-bold ${d.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>{formatCurrency(d.balance)}</p>
                      <p className="text-xs text-gray-500">Balance</p>
                    </div>
                  </div>
                )
              })()}

              {/* Invoices */}
              <Card>
                <CardHeader title="Invoices" subtitle={`${doctorInvoices?.length ?? 0} invoice(s)`} />
                <div className="divide-y divide-gray-200 dark:divide-gray-700 mt-2">
                  {doctorInvoices?.map((invoice) => (
                    <div key={invoice.id} className="py-3 flex items-center justify-between">
                      <div>
                        <Link
                          to={`/invoices/${invoice.id}`}
                          className="font-medium text-primary-600 hover:underline"
                        >
                          {invoice.displayId}
                        </Link>
                        <p className="text-sm text-gray-500">
                          Issued: {formatDate(invoice.issueDate)} • Due: {formatDate(invoice.dueDate)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {formatCurrency(invoice.totalAmount)}
                        </p>
                        {invoice.balanceAmount > 0 ? (
                          <p className="text-sm text-red-600">
                            Due: {formatCurrency(invoice.balanceAmount)}
                          </p>
                        ) : (
                          <Badge variant="success">Paid</Badge>
                        )}
                      </div>
                    </div>
                  )) ?? null}
                  {!doctorInvoices?.length && (
                    <p className="py-4 text-center text-gray-500">No invoices found</p>
                  )}
                </div>
              </Card>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => {
                    window.print()
                    toast.success('Opening print dialog...')
                  }}
                >
                  <i className="fas fa-download mr-2"></i>Print
                </Button>
                <Link to={`/payments?new=1&doctorId=${selectedDoctor.id}`} className="flex-1">
                  <Button className="w-full">
                    <i className="fas fa-plus mr-2"></i>Record Payment
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
